import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2, X } from "lucide-react";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { Logo } from "@/components/Logo";
import { WeeklyAlbumPicker, AlbumChip } from "@/components/weekly/WeeklyAlbumPicker";
import { WeeklySlidesViewer } from "@/components/weekly/WeeklySlidesViewer";
import { saveWeeklyStory } from "@/lib/weekly.functions";
import {
  currentWeek,
  emptyWeekly,
  hasAnyAnswer,
  weekLabel,
  type WeeklyData,
} from "@/lib/weekly-types";
import FluidFieldBackground from "@/components/ui/fluid-field";

export const Route = createFileRoute("/semanal")({
  head: () => ({
    meta: [
      { title: "Viny Semanal — sua semana em três discos" },
      {
        name: "description",
        content: "Escolha três discos da sua semana e crie imagens prontas para compartilhar nos Stories.",
      },
      { property: "og:title", content: "Viny Semanal — sua semana em três discos" },
      {
        property: "og:description",
        content: "O disco mais ouvido, o que chegou e a prioridade do próximo garimpo.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: WeeklyPage,
});

const STORAGE_KEY = "viny-semanal-draft-v1";
type Phase = "intro" | "form" | "result";

type StepDef = {
  title: string;
  hint: string;
  value: boolean;
  body: React.ReactNode;
};

function buildSteps(data: WeeklyData, patch: (next: Partial<WeeklyData>) => void): StepDef[] {
  const hunts = (data.newAlbums ?? []).slice(0, 5);
  return [
    {
      title: "Qual disco não saiu da vitrola?",
      hint: "O disco que definiu sua semana.",
      value: Boolean(data.mostPlayed),
      body: data.mostPlayed ? (
        <AlbumChip album={data.mostPlayed} onRemove={() => patch({ mostPlayed: null })} />
      ) : (
        <WeeklyAlbumPicker onPick={(album) => patch({ mostPlayed: album })} />
      ),
    },
    {
      title: "Garimpos da semana",
      hint: "Você pode escolher até 5 discos que chegaram nesta semana.",
      value: hunts.length > 0,
      body: (
        <div className="flex min-w-0 flex-col gap-3">
          {hunts.length > 0 && (
            <div className="flex flex-col gap-2">
              {hunts.map((album, index) => (
                <div key={`${album.discogsId ?? album.title}-${index}`} className="grid min-w-0 grid-cols-[36px_minmax(0,1fr)] items-center gap-2">
                  <span className="text-sm font-semibold text-muted-foreground">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <AlbumChip
                    album={album}
                    onRemove={() =>
                      patch({ newAlbums: hunts.filter((_, i) => i !== index), favoriteIndex: 0 })
                    }
                  />
                </div>
              ))}
            </div>
          )}
          {hunts.length < 5 ? (
            <WeeklyAlbumPicker
              onPick={(album) => patch({ newAlbums: [...hunts, album], favoriteIndex: 0 })}
              placeholder={hunts.length ? "Adicionar outro garimpo…" : "Buscar o disco que chegou…"}
            />
          ) : (
            <p className="text-xs text-muted-foreground">Limite de 5 discos alcançado.</p>
          )}
        </div>
      ),
    },
    {
      title: "Qual disco é prioridade no próximo garimpo?",
      hint: "Aquele que você quer encontrar antes de todos os outros.",
      value: Boolean(data.wishlist),
      body: data.wishlist ? (
        <AlbumChip album={data.wishlist} onRemove={() => patch({ wishlist: null })} />
      ) : (
        <WeeklyAlbumPicker onPick={(album) => patch({ wishlist: album })} placeholder="Buscar disco…" />
      ),
    },
  ];
}

function WeeklyPage() {
  const { profile } = useCurrentUser();
  const [phase, setPhase] = useState<Phase>("intro");
  const [data, setData] = useState<WeeklyData>(() => emptyWeekly());
  const [step, setStep] = useState(0);
  const [restored, setRestored] = useState(false);
  const save = useServerFn(saveWeeklyStory);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as WeeklyData;
      const week = currentWeek();
      if (parsed?.weekStart === week.start) {
        setData({ ...emptyWeekly(), ...parsed });
        if (hasAnyAnswer(parsed)) setRestored(true);
      }
    } catch {
      // Um rascunho inválido não impede um novo preenchimento.
    }
  }, []);

  useEffect(() => {
    if (profile?.username && data.username !== profile.username) {
      setData((current) => ({ ...current, username: profile.username }));
    }
  }, [profile?.username, data.username]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {
      // O fluxo continua mesmo sem armazenamento local disponível.
    }
  }, [data]);

  const patch = useCallback((next: Partial<WeeklyData>) => {
    setData((current) => ({ ...current, ...next }));
  }, []);
  const saveMutation = useMutation({
    mutationFn: (payload: WeeklyData) => save({ data: { data: payload } }),
  });
  const steps = useMemo(() => buildSteps(data, patch), [data, patch]);
  const current = steps[Math.min(step, steps.length - 1)];
  const complete = steps.every((item) => item.value);

  const finish = () => {
    if (!complete) {
      toast.error("Escolha os três discos para montar sua semana.");
      return;
    }
    if (profile) saveMutation.mutate(data, { onError: () => undefined });
    setPhase("result");
    window.scrollTo({ top: 0 });
  };

  const next = () => {
    if (!current.value) {
      toast.error("Escolha um disco para continuar.");
      return;
    }
    if (step < steps.length - 1) setStep((value) => value + 1);
    else finish();
  };

  if (phase === "result") {
    return (
      <ResultScreen
        data={data}
        saving={saveMutation.isPending}
        onEdit={() => {
          setPhase("form");
          setStep(0);
        }}
      />
    );
  }

  return (
    <main className="h-[100dvh] overflow-hidden bg-background text-foreground lg:grid lg:grid-cols-[minmax(360px,0.85fr)_minmax(520px,1.15fr)]">
      <section className="relative hidden overflow-hidden lg:block">
        <FluidFieldBackground className="absolute inset-0 h-full w-full" />
        <div className="absolute inset-0 bg-gradient-to-r from-background/10 to-background" />
        <div className="absolute left-8 top-8"><Logo variant="light" size={30} /></div>
        <p className="absolute bottom-8 left-8 text-sm text-foreground/65">
          {weekLabel(data.weekStart, data.weekEnd).toLowerCase()}
        </p>
      </section>

      <section className="relative flex h-[100dvh] min-w-0 flex-col overflow-hidden bg-background">
        <header className="grid h-16 shrink-0 grid-cols-[minmax(0,1fr)_auto] items-center border-b border-border px-4 lg:h-20 lg:px-10">
          <div className="min-w-0 lg:hidden"><Logo variant="light" size={24} /></div>
          <div className="hidden min-w-0 lg:block">
            <span className="text-sm text-muted-foreground">Viny Semanal</span>
          </div>
          <Link
            to="/explore"
            aria-label="Sair"
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-border bg-surface"
          >
            <X className="h-4 w-4" />
          </Link>
        </header>

        {phase === "intro" ? (
          <IntroScreen
            weekText={weekLabel(data.weekStart, data.weekEnd)}
            restored={restored}
            onStart={() => {
              setPhase("form");
              const firstMissing = steps.findIndex((item) => !item.value);
              setStep(firstMissing >= 0 ? firstMissing : 0);
            }}
            onSeeResult={complete ? () => setPhase("result") : undefined}
          />
        ) : (
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="shrink-0 px-4 pt-5 lg:px-12 lg:pt-8">
              <div className="grid grid-cols-3 gap-2" aria-label={`Etapa ${step + 1} de 3`}>
                {steps.map((item, index) => (
                  <span
                    key={item.title}
                    className={`h-1 rounded-full ${index <= step ? "bg-primary" : "bg-border"}`}
                  />
                ))}
              </div>
              <p className="mt-3 text-xs text-muted-foreground">{step + 1} de 3</p>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-4 pt-6 lg:px-12 lg:pt-10">
              <div className="mx-auto w-full max-w-2xl">
                <h1 className="max-w-xl font-display text-4xl font-semibold leading-tight sm:text-5xl lg:text-6xl">
                  {current.title}
                </h1>
                <p className="mt-3 max-w-lg text-sm leading-relaxed text-muted-foreground sm:text-base">
                  {current.hint}
                </p>
                <div className="mt-7 min-w-0">{current.body}</div>
              </div>
            </div>

            <footer className="shrink-0 border-t border-border bg-background px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3 lg:px-12 lg:py-5">
              <div className="mx-auto grid w-full max-w-2xl grid-cols-[48px_minmax(0,1fr)] gap-3">
                <button
                  type="button"
                  onClick={() => (step > 0 ? setStep((value) => value - 1) : setPhase("intro"))}
                  aria-label="Voltar"
                  className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-border"
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={next}
                  disabled={!current.value}
                  className="inline-flex h-12 min-w-0 items-center justify-center gap-2 rounded-full bg-primary px-5 text-base font-bold text-primary-foreground disabled:opacity-35"
                >
                  {step === steps.length - 1 ? "Criar minhas imagens" : "Continuar"}
                  <ArrowRight className="h-4 w-4 shrink-0" />
                </button>
              </div>
            </footer>
          </div>
        )}
      </section>
    </main>
  );
}

function IntroScreen({
  weekText,
  restored,
  onStart,
  onSeeResult,
}: {
  weekText: string;
  restored: boolean;
  onStart: () => void;
  onSeeResult?: () => void;
}) {
  return (
    <div className="flex min-h-0 flex-1 flex-col justify-center overflow-y-auto px-5 py-8 lg:px-12">
      <div className="mx-auto w-full max-w-2xl">
        <span className="text-sm text-muted-foreground">{weekText.toLowerCase()}</span>
        <h1 className="mt-4 max-w-xl font-display text-5xl font-semibold leading-[1.04] sm:text-6xl lg:text-7xl">
          Sua semana em três discos.
        </h1>
        <p className="mt-5 max-w-md text-base leading-relaxed text-muted-foreground">
          O mais ouvido, o que chegou e sua prioridade no próximo garimpo.
        </p>
        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          <button
            type="button"
            onClick={onStart}
            className="inline-flex h-14 items-center justify-center gap-2 rounded-full bg-primary px-8 text-base font-bold text-primary-foreground"
          >
            {restored ? "Continuar" : "Começar"}
            <ArrowRight className="h-4 w-4" />
          </button>
          {onSeeResult && (
            <button
              type="button"
              onClick={onSeeResult}
              className="inline-flex h-14 items-center justify-center rounded-full border border-border px-6 text-sm font-semibold"
            >
              Ver minhas imagens
            </button>
          )}
        </div>
        <p className="mt-4 text-xs text-muted-foreground">leva menos de um minuto</p>
      </div>
    </div>
  );
}

function ResultScreen({ data, saving, onEdit }: { data: WeeklyData; saving: boolean; onEdit: () => void }) {
  const [ready, setReady] = useState(false);
  const timer = useRef<number | null>(null);
  useEffect(() => {
    timer.current = window.setTimeout(() => setReady(true), 300);
    return () => {
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, []);

  return (
    <main className="min-h-[100dvh] overflow-x-hidden bg-background px-4 pb-12 pt-5 text-foreground sm:px-6 lg:px-8 lg:pt-8">
      <div className="mx-auto w-full max-w-[980px]">
        <div className="mb-7 grid grid-cols-[minmax(0,1fr)_auto] items-start gap-4">
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground">viny semanal · {weekLabel(data.weekStart, data.weekEnd).toLowerCase()}</p>
            <h1 className="mt-2 font-display text-4xl font-semibold leading-tight sm:text-5xl">Prontas para compartilhar.</h1>
            <p className="mt-2 text-sm text-muted-foreground">Três discos, três imagens. {saving ? "Salvando…" : ""}</p>
          </div>
          <button
            type="button"
            onClick={onEdit}
            className="inline-flex h-10 shrink-0 items-center justify-center rounded-full border border-border px-4 text-sm font-semibold"
          >
            Editar
          </button>
        </div>
        {ready ? (
          <WeeklySlidesViewer data={data} />
        ) : (
          <div className="flex flex-col items-center gap-4 py-24 text-muted-foreground">
            <Loader2 className="h-7 w-7 animate-spin" />
            <p className="text-sm">Criando suas imagens…</p>
          </div>
        )}
      </div>
    </main>
  );
}
